import { useState, useEffect, useRef } from "react";

/**
 * Custom hook: Anti-cheat monitor.
 * Listens to visibilitychange + window blur events to detect tab switching.
 * Uses the "ref sync" pattern so listeners are registered only once (stable),
 * while always calling the latest callbacks.
 *
 * @param {boolean} isActive - True when monitoring should be on (during exam)
 * @param {object} handlers - { onFirstViolation, onSecondViolation }
 */
export function useAntiCheat(isActive, { onFirstViolation, onSecondViolation } = {}) {
  const [violationCount, setViolationCount] = useState(0);
  const violationCountRef = useRef(0);
  const isActiveRef = useRef(isActive);
  const handlersRef = useRef({ onFirstViolation, onSecondViolation });

  // Sync refs every render so listeners always call the latest handlers
  useEffect(() => {
    isActiveRef.current = isActive;
    handlersRef.current = { onFirstViolation, onSecondViolation };
  });

  // Register event listeners exactly once — refs handle freshness
  useEffect(() => {
    let lastViolationTime = 0;

    const triggerViolation = () => {
      if (!isActiveRef.current) return;
      const now = Date.now();
      if (now - lastViolationTime < 1500) return; // debounce duplicate events
      lastViolationTime = now;

      violationCountRef.current += 1;
      const count = violationCountRef.current;
      setViolationCount(count);

      if (count >= 2) {
        handlersRef.current.onSecondViolation?.();
      } else {
        handlersRef.current.onFirstViolation?.();
      }
    };

    const handleVisibility = () => { if (document.hidden) triggerViolation(); };
    const handleBlur = () => triggerViolation();

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("blur", handleBlur);
    };
  }, []); // Intentional empty array — listeners registered once, refs provide fresh values

  const resetViolations = () => {
    violationCountRef.current = 0;
    setViolationCount(0);
  };

  return { violationCount, resetViolations };
}
